import type { CloudApiResponse } from '@scada-ink/shared/cloud';

/**
 * The one way the site talks to the cloud service.
 *
 * `credentials: 'include'` carries the httpOnly session cookie. In development
 * Vite proxies /api/portal and /api/public so the cookie is same-origin; in
 * production VITE_CLOUD_URL points at cloud.scada.ink, which allow-lists this
 * origin for credentialed CORS.
 */

const BASE = (import.meta.env.VITE_CLOUD_URL || '').replace(/\/+$/, '');

export class CloudApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      credentials: 'include',
      headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new CloudApiError('Could not reach Scada.Ink Cloud. Check your connection and try again.', 0);
  }

  let payload: CloudApiResponse<T> | null = null;
  try {
    payload = (await res.json()) as CloudApiResponse<T>;
  } catch {
    // Non-JSON (a proxy error page, say) — reported by status below.
  }

  if (!payload) {
    throw new CloudApiError(`Scada.Ink Cloud returned an unexpected response (${res.status}).`, res.status);
  }
  if (!payload.success) {
    throw new CloudApiError(payload.error || `Request failed (${res.status})`, res.status);
  }
  return payload.data;
}

export const cloudApi = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body: unknown = {}) => request<T>('POST', path, body),
  put: <T>(path: string, body: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body: unknown) => request<T>('PATCH', path, body),
  del: <T>(path: string) => request<T>('DELETE', path),
};

export function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : 'Something went wrong. Please try again.';
}
